import React, {useEffect, useImperativeHandle, useRef, useState} from 'react';
import {
    Box,
    Typography,
    Alert,
    Autocomplete,
    TextField,
    CircularProgress,
    Checkbox,
    Stack,
    MenuItem,
    Select,
    Chip,
    FormControlLabel
} from '@mui/material';
import PropTypes from 'prop-types';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import {useGetFormAccessQuery, useManageFormAccessMutation} from '../../../api/services/formsService';
import {useGetActiveUsersQuery} from '../../../api/services/accountService';
import {ROLES, ROLES_LABELS} from '../../../constants/roles';
import useNotification from '../../../utility/hooks/useNotification';
import SettingsSectionBase from './PrivateFormSettings/SettingsSection';
import useUser from '../../../utility/hooks/useUser';

const FormAccessSettings = React.forwardRef(({
                                                 formId,
                                                 formName,
                                                 isFormCreation = false,
                                                 wizardMode = false,
                                                 embedded = false,
                                                 autoSave = false,
                                                 pending = null
                                             }, ref) => {
    const [ownUsers, setOwnUsers] = useState([]);
    const [ownRoleNames, setOwnRoleNames] = useState([]);
    // AD-2689: in the Create Form box (pending), the choices are kept by the box and saved when the form is created.
    const selectedUsers = pending ? pending.users : ownUsers;
    const selectedRoleNames = pending ? pending.roles : ownRoleNames;
    const setSelectedUsers = pending ? (users) => pending.onChange({...pending, users}) : setOwnUsers;
    const setSelectedRoleNames = pending ? (roles) => pending.onChange({...pending, roles}) : setOwnRoleNames;
    // AD-2694: the people and roles with View access only; everyone else has Edit access.
    const [ownViewOnly, setOwnViewOnly] = useState({users: [], roles: []});
    const viewOnly = pending
        ? {users: pending.viewOnlyUsers ?? [], roles: pending.viewOnlyRoles ?? []}
        : ownViewOnly;
    const setViewOnly = pending
        ? (next) => pending.onChange({...pending, viewOnlyUsers: next.users, viewOnlyRoles: next.roles})
        : setOwnViewOnly;
    // AD-2777: the creator lets other School Admins with Edit access change these settings. Off on every form.
    const [ownAllowSchoolAdmins, setOwnAllowSchoolAdmins] = useState(false);
    const allowSchoolAdmins = pending ? Boolean(pending.allowSchoolAdmins) : ownAllowSchoolAdmins;
    const setAllowSchoolAdmins = pending
        ? (value) => pending.onChange({...pending, allowSchoolAdmins: value})
        : setOwnAllowSchoolAdmins;
    const viewOnlyPrefilledRef = useRef(false);
    const usersPrefilledRef = useRef(false);
    const rolesPrefilledRef = useRef(false);
    const [manageFormAccess, {isLoading: isManaging}] = useManageFormAccessMutation();
    const {
        showSuccessNotification,
        showErrorNotification
    } = useNotification();

    const {
        data: availableUsers = [],
        isLoading: isLoadingUsers
    } = useGetActiveUsersQuery({pageSize: 0}, {
        skip: !formId && !pending
    });

    // AD-2704: in the Create Form box, the member of staff creating the form is shown in 'Select Users' from the
    // start, as Form Settings shows them once the form exists. The server gives them access either way.
    const {user: signedInUser} = useUser();
    const creatorPrefilledRef = useRef(false);
    useEffect(() => {
        if (!pending || creatorPrefilledRef.current || !availableUsers.length || !signedInUser?.id) return;
        creatorPrefilledRef.current = true;
        const creator = availableUsers.find(user => user.id === signedInUser.id);
        if (creator && !pending.users.some(user => user.id === creator.id)) {
            pending.onChange({...pending, users: [creator, ...pending.users]});
        }
    }, [pending, availableUsers, signedInUser?.id]);

    const filteredRoleNames = [
        ROLES.SCHOOL_ADMIN,
        ROLES.MEMBER_OF_STAFF,
        ROLES.SENIOR_MEMBER_OF_STAFF
    ];

    const {
        data: formAccess = {},
        isLoading: isLoadingFormAccess
    } = useGetFormAccessQuery(formId, {
        skip: !formId
    });

    const accessEntry = Array.isArray(formAccess)
        ? (formAccess[0] || {})
        : (formAccess || {});
    const serverUserIds = Array.isArray(accessEntry.userIds)
        ? accessEntry.userIds
        : (Array.isArray(accessEntry.users) ? accessEntry.users : []);

    const serverRoleNames = Array.isArray(accessEntry.roles)
        ? accessEntry.roles
        : [];

    // AD-2776: the creator's row is always Edit and cannot be removed. In the Create Form box the creator is the
    // person creating the form. AD-2777: only the creator changes Access, unless they let other School Admins.
    const creatorId = pending ? signedInUser?.id : accessEntry.creatorUserId;
    const isCreator = pending ? true : Boolean(accessEntry.isCreator);
    const canManage = pending ? true : Boolean(accessEntry.canManageAccess);
    const locked = isManaging || (!formId && !pending) || !canManage;

    useEffect(() => {
        if (formId) {
            setSelectedUsers([]);
            setSelectedRoleNames([]);
            usersPrefilledRef.current = false;
            rolesPrefilledRef.current = false;
            viewOnlyPrefilledRef.current = false;
        }
    }, [formId]);

    useEffect(() => {
        if (formId && !viewOnlyPrefilledRef.current && !isLoadingFormAccess && accessEntry &&
            (accessEntry.userIds || accessEntry.roles)) {
            setOwnViewOnly({users: accessEntry.viewOnlyUserIds ?? [], roles: accessEntry.viewOnlyRoles ?? []});
            setOwnAllowSchoolAdmins(Boolean(accessEntry.allowSchoolAdminsToManageAccess));
            viewOnlyPrefilledRef.current = true;
        }
    }, [formId, isLoadingFormAccess, accessEntry]);

    useEffect(() => {
        if (
            formId &&
            !usersPrefilledRef.current &&
            selectedUsers.length === 0 &&
            availableUsers.length > 0 &&
            serverUserIds.length > 0
        ) {
            const currentUsers = serverUserIds
                .map(userId => availableUsers.find(user => user.id === userId))
                .filter(Boolean);
            setSelectedUsers(currentUsers);
            usersPrefilledRef.current = true;
        }
    }, [formId, availableUsers, serverUserIds, selectedUsers.length]);

    useEffect(() => {
        if (
            formId &&
            !rolesPrefilledRef.current &&
            selectedRoleNames.length === 0 &&
            filteredRoleNames.length > 0 &&
            serverRoleNames.length > 0
        ) {
            const allowedLower = filteredRoleNames.map(n => String(n).toLowerCase());
            const currentRoleNames = serverRoleNames
                .filter(roleName => allowedLower.includes(String(roleName).toLowerCase()));
            setSelectedRoleNames(currentRoleNames);
            rolesPrefilledRef.current = true;
        }
    }, [formId, filteredRoleNames, serverRoleNames, selectedRoleNames.length]);

    const saveAccess = async ({
                                  users = selectedUsers,
                                  roles = selectedRoleNames,
                                  viewOnlyLists = viewOnly,
                                  allowAdmins = allowSchoolAdmins,
                                  suppressSuccessNotification = false
                              } = {}) => {
        if (!formId) {
            return;
        }

        if (users.length === 0 && roles.length === 0) {
            showErrorNotification('Choose at least one member of staff or role.');
            throw new Error('Choose at least one member of staff or role.');
        }

        try {
            const userIds = users.map(user => user.id);

            await manageFormAccess({
                formId,
                userIds,
                roles,
                viewOnlyUserIds: viewOnlyLists.users.filter(id => userIds.includes(id)),
                viewOnlyRoles: viewOnlyLists.roles.filter(role => roles.includes(role)),
                ...(isCreator ? {allowSchoolAdminsToManageAccess: allowAdmins} : {}),
            }).unwrap();
            if (!wizardMode && !suppressSuccessNotification) {
                showSuccessNotification('Form access updated successfully');
            }
        } catch (error) {
            showErrorNotification('Failed to update form access');
            throw error;
        }
    };

    const handleSave = async ({suppressSuccessNotification = false} = {}) =>
        saveAccess({suppressSuccessNotification});

    // AD-2689: a change of people, roles or View/Edit counts as an unsaved change of the form, so 'Save and Exit
    // Form Builder' saves it on a live form.
    const sameList = (a = [], b = []) =>
        a.length === b.length && [...a].map(String).sort().join('|') === [...b].map(String).sort().join('|');
    const hasChanges = () => {
        if (!formId || pending || isLoadingFormAccess) return false;
        const userIds = selectedUsers.map(user => user.id);
        const allowedLower = filteredRoleNames.map(n => String(n).toLowerCase());
        const shownServerRoles = serverRoleNames.filter(role => allowedLower.includes(String(role).toLowerCase()));
        return !sameList(userIds, serverUserIds) ||
            !sameList(selectedRoleNames, shownServerRoles) ||
            !sameList(viewOnly.users.filter(id => userIds.includes(id)), accessEntry.viewOnlyUserIds ?? []) ||
            !sameList(viewOnly.roles.filter(role => selectedRoleNames.includes(role)), accessEntry.viewOnlyRoles ?? []) ||
            allowSchoolAdmins !== Boolean(accessEntry.allowSchoolAdminsToManageAccess);
    };

    useImperativeHandle(ref, () => ({
        saveAccessSettings: handleSave,
        hasChanges
    }));

    const content = (
        <Stack spacing={wizardMode ? 2.5 : 3}>
            {!wizardMode && !embedded && (
                <Box>
                    <Typography variant="h6" gutterBottom>
                        Form Access Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage which users and roles have access to this form. This controls who can view the form,
                        access form responses, and manage the form settings.
                    </Typography>
                </Box>
            )}

            {!formId && embedded && !pending && (
                <Alert severity="info">
                    Save the form first to manage which users and roles can access it.
                </Alert>
            )}

            <Box>
                {!wizardMode && (
                    <Typography variant="subtitle2" sx={{
                        mb: 1,
                        fontWeight: 'bold'
                    }}>
                        Users
                    </Typography>
                )}
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    limitTags={5}
                    options={availableUsers}
                    getOptionLabel={(option) => option.name || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={selectedUsers}
                    onChange={async (event, newValue) => {
                        if (creatorId && selectedUsers.some(user => user.id === creatorId) &&
                            !newValue.some(user => user.id === creatorId)) {
                            showErrorNotification('The form creator\'s access cannot be changed.');
                            return;
                        }
                        if (newValue.length === 0 && selectedUsers.length > 0 && selectedRoleNames.length === 0) {
                            showErrorNotification('At least one member of staff or role must have access to the form.');
                            return;
                        }
                        setSelectedUsers(newValue);

                        if (autoSave) {
                            await saveAccess({
                                users: newValue,
                                roles: selectedRoleNames,
                            });
                        }
                    }}
                    loading={isLoadingUsers || isLoadingFormAccess}
                    disabled={locked}
                    renderTags={(value, getTagProps) => value.map((option, index) => {
                        const {key, onDelete, ...tagProps} = getTagProps({index});
                        const isCreatorTag = option.id === creatorId;
                        return (
                            <Chip key={key} size="small" label={isCreatorTag ? `${option.name} (creator)` : option.name}
                                  {...tagProps} onDelete={isCreatorTag ? undefined : onDelete}/>
                        );
                    })}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Select Users"
                            placeholder="Start typing a name..."
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {(isLoadingUsers || isLoadingFormAccess) ? <CircularProgress size={20}/> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    renderOption={(props, option, {selected}) => (
                        <li {...props} key={option.id}>
                            <Checkbox
                                icon={<CheckBoxOutlineBlankIcon fontSize="small"/>}
                                checkedIcon={<CheckBoxIcon fontSize="small"/>}
                                checked={selected}
                            />
                            {option.name}
                        </li>
                    )}
                />
            </Box>

            <Box>
                {!wizardMode && (
                    <Typography variant="subtitle2" sx={{
                        mb: 1,
                        fontWeight: 'bold'
                    }}>
                        Roles
                    </Typography>
                )}
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    limitTags={5}
                    options={filteredRoleNames}
                    getOptionLabel={(name) => ROLES_LABELS[name] || name || ''}
                    isOptionEqualToValue={(option, value) => String(option || '').toLowerCase() === String(value || '').toLowerCase()}
                    value={selectedRoleNames}
                    onChange={async (event, newValue) => {
                        if (newValue.length === 0 && selectedRoleNames.length > 0 && selectedUsers.length === 0) {
                            showErrorNotification('At least one member of staff or role must have access to the form.');
                            return;
                        }
                        setSelectedRoleNames(newValue);

                        if (autoSave) {
                            await saveAccess({
                                users: selectedUsers,
                                roles: newValue,
                            });
                        }
                    }}
                    loading={isLoadingFormAccess}
                    disabled={locked}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Select Roles"
                            placeholder="Select roles..."
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {isLoadingFormAccess ? <CircularProgress size={20}/> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    renderOption={(props, name, {selected}) => (
                        <li {...props} key={name}>
                            <Checkbox
                                icon={<CheckBoxOutlineBlankIcon fontSize="small"/>}
                                checkedIcon={<CheckBoxIcon fontSize="small"/>}
                                checked={selected}
                            />
                            {ROLES_LABELS[name] || name}
                        </li>
                    )}
                />
            </Box>
            {(selectedUsers.length > 0 || selectedRoleNames.length > 0) && (
                <Box data-testid="form-access-levels">
                    <Typography variant="subtitle2" sx={{mb: 0.5, fontWeight: 'bold'}}>
                        View or Edit
                    </Typography>
                    <Stack spacing={0.5}>
                        {[
                            ...selectedUsers.map(user => ({key: user.id, kind: 'users', label: user.name})),
                            ...selectedRoleNames.map(role => ({key: role, kind: 'roles', label: ROLES_LABELS[role] || role})),
                        ].map(item => {
                            const isViewOnly = viewOnly[item.kind].includes(item.key);
                            const isCreatorRow = item.kind === 'users' && item.key === creatorId;
                            return (
                                <Stack key={`${item.kind}-${item.key}`} direction="row" alignItems="center"
                                       justifyContent="space-between"
                                       sx={{px: 1.5, py: 0.25, border: '1px solid #e3e8ef', borderRadius: 2}}>
                                    <Typography variant="body2">
                                        {isCreatorRow ? `${item.label} (creator)` : item.label}
                                    </Typography>
                                    {isCreatorRow && (
                                        <Typography variant="body2" sx={{minWidth: 110, px: 1.75, lineHeight: '40px'}}>Edit</Typography>
                                    )}
                                    {!isCreatorRow && (
                                    <Select
                                        size="small"
                                        value={isViewOnly ? 'view' : 'edit'}
                                        disabled={locked}
                                        inputProps={{'aria-label': `Access for ${item.label}`}}
                                        onChange={async (event) => {
                                            const others = viewOnly[item.kind].filter(key => key !== item.key);
                                            const next = {
                                                ...viewOnly,
                                                [item.kind]: event.target.value === 'view' ? [...others, item.key] : others,
                                            };
                                            setViewOnly(next);
                                            if (autoSave) {
                                                await saveAccess({viewOnlyLists: next});
                                            }
                                        }}
                                        sx={{minWidth: 110}}
                                    >
                                        <MenuItem value="edit">Edit</MenuItem>
                                        <MenuItem value="view">View</MenuItem>
                                    </Select>
                                    )}
                                </Stack>
                            );
                        })}
                    </Stack>
                </Box>
            )}
            {(formId || pending) && (
                <FormControlLabel
                    data-testid="form-access-allow-school-admins"
                    control={
                        <Checkbox
                            checked={allowSchoolAdmins}
                            disabled={isManaging || !isCreator}
                            onChange={async (event) => {
                                const value = event.target.checked;
                                setAllowSchoolAdmins(value);
                                if (autoSave) {
                                    await saveAccess({allowAdmins: value});
                                }
                            }}
                        />
                    }
                    label="Allow other School Admins to change these Access settings (the form creator's access cannot be changed)"
                />
            )}
        </Stack>
    );

    if (embedded) {
        return (
            <SettingsSectionBase
                header="Form Access"
                headerIcon={<AdminPanelSettingsOutlinedIcon fontSize="small"/>}
                // AD-2689 (Maria, 14 October 2026): the note about groups is explained at the top, not in a box.
                subHeader="You can allow access to any staff who need it. Staff can only use School Groups shared with them."
            >
                {content}
            </SettingsSectionBase>
        );
    }

    return (
        <Box sx={{p: wizardMode ? 0 : 3}}>
            {content}
        </Box>
    );
});

FormAccessSettings.displayName = 'FormAccessSettings';

FormAccessSettings.propTypes = {
    formId: PropTypes.number,
    formName: PropTypes.string,
    isFormCreation: PropTypes.bool,
    wizardMode: PropTypes.bool,
    embedded: PropTypes.bool,
    autoSave: PropTypes.bool,
    pending: PropTypes.shape({
        users: PropTypes.array,
        roles: PropTypes.array,
        viewOnlyUsers: PropTypes.array,
        viewOnlyRoles: PropTypes.array,
        allowSchoolAdmins: PropTypes.bool,
        onChange: PropTypes.func
    })
};

export default FormAccessSettings;
